import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, FloppyDisk, Plus, Trash, DotsSixVertical, Globe, MagnifyingGlass,
} from "@phosphor-icons/react";

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tmpl, setTmpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragItem = useRef(null);

  useEffect(() => { api.get(`/templates/${id}`).then((r) => setTmpl(r.data)); }, [id]);
  if (!tmpl) return <div className="text-slate-500">Loading template…</div>;

  const moveSection = (from, to) => {
    if (from === null || to === null || from === to) return;
    setTmpl((t) => {
      const arr = [...t.sections];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return { ...t, sections: arr };
    });
  };

  const update = (patch) => setTmpl((t) => ({ ...t, ...patch }));
  const updateSection = (idx, patch) =>
    setTmpl((t) => ({ ...t, sections: t.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  const addSection = () =>
    setTmpl((t) => ({ ...t, sections: [...t.sections, {
      id: `sec-${Date.now()}`, title: "New Section", description: "", source_label: "",
      source_urls: [], use_web_search: false, content: "", order: t.sections.length }] }));
  const removeSection = (idx) => setTmpl((t) => ({ ...t, sections: t.sections.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: tmpl.name, description: tmpl.description, department: tmpl.department, subtitle: tmpl.subtitle,
        sections: tmpl.sections.map((s, i) => ({
          ...s,
          source_urls: typeof s.source_urls === "string" ? s.source_urls.split(",").map((u) => u.trim()).filter(Boolean) : s.source_urls,
          order: i,
        })),
      };
      await api.put(`/templates/${id}`, payload);
      toast.success("Template saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const inputCls = "bg-white";

  return (
    <div className="space-y-6" data-testid="template-editor-page">
      <div className="flex items-center justify-between gap-4">
        <button onClick={() => navigate("/templates")} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm" data-testid="editor-back">
          <ArrowLeft size={18} /> Templates
        </button>
        <Button data-testid="save-template-btn" onClick={save} disabled={saving} className="gold-btn rounded-xl px-5 h-11 font-semibold gap-2">
          <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save Template"}
        </Button>
      </div>

      <div className="card p-6 space-y-4">
        <div><div className="label-caps text-slate-500 mb-1.5">Template Name</div><Input data-testid="tmpl-name" value={tmpl.name} onChange={(e) => update({ name: e.target.value })} className={inputCls} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="label-caps text-slate-500 mb-1.5">Department</div><Input data-testid="tmpl-dept" value={tmpl.department || ""} onChange={(e) => update({ department: e.target.value })} className={inputCls} /></div>
          <div><div className="label-caps text-slate-500 mb-1.5">Subtitle</div><Input data-testid="tmpl-subtitle" value={tmpl.subtitle || ""} onChange={(e) => update({ subtitle: e.target.value })} className={inputCls} /></div>
        </div>
        <div><div className="label-caps text-slate-500 mb-1.5">Description</div><Textarea data-testid="tmpl-desc" value={tmpl.description || ""} onChange={(e) => update({ description: e.target.value })} className={inputCls} /></div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Sections ({tmpl.sections.length})</h2>
        <Button data-testid="add-section-btn" onClick={addSection} className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl gap-2 h-11"><Plus size={16} /> Add Section</Button>
      </div>
      <p className="text-xs text-slate-500 -mt-2 flex items-center gap-1.5"><DotsSixVertical size={15} className="text-slate-400" /> Drag the handle on any section to reorder. The order here is the order in the generated report.</p>

      <div className="space-y-4">
        {tmpl.sections.map((s, idx) => (
          <div key={s.id} data-testid={`section-editor-${idx}`}
            onDragOver={(e) => { if (dragItem.current !== null) { e.preventDefault(); setOverIdx(idx); } }}
            onDrop={(e) => { e.preventDefault(); moveSection(dragItem.current, idx); dragItem.current = null; setDragIdx(null); setOverIdx(null); }}
            className={`card p-5 transition-all ${dragIdx === idx ? "opacity-40" : ""} ${overIdx === idx && dragIdx !== idx ? "ring-2 ring-[#F59E0B] border-[#F59E0B]" : ""}`}>
            <div className="flex items-center gap-3 mb-4">
              <div draggable data-testid={`section-drag-${idx}`}
                onDragStart={() => { dragItem.current = idx; setDragIdx(idx); }}
                onDragEnd={() => { dragItem.current = null; setDragIdx(null); setOverIdx(null); }}
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#F59E0B] transition-colors" title="Drag to reorder">
                <DotsSixVertical size={22} weight="bold" />
              </div>
              <span className="text-xs text-slate-400">#{idx + 1}</span>
              <Input data-testid={`section-title-${idx}`} value={s.title} onChange={(e) => updateSection(idx, { title: e.target.value })} className={`${inputCls} font-semibold flex-1`} placeholder="Section title" />
              <Button variant="ghost" data-testid={`remove-section-${idx}`} onClick={() => removeSection(idx)} className="text-slate-400 hover:text-red-500"><Trash size={16} /></Button>
            </div>
            <div className="grid gap-4">
              <div><div className="label-caps text-slate-500 mb-1.5">Expected content / format guidance</div>
                <Textarea data-testid={`section-desc-${idx}`} value={s.description || ""} onChange={(e) => updateSection(idx, { description: e.target.value })} className={`${inputCls} min-h-[70px]`} placeholder="Describe what this section should contain…" /></div>
              <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
                <div className="flex items-center gap-2 mb-3"><Globe size={16} className="text-purple-600" /><span className="text-sm font-medium text-purple-700">Live Data Source</span></div>
                <div className="grid gap-3">
                  <Input data-testid={`section-source-label-${idx}`} value={s.source_label || ""} onChange={(e) => updateSection(idx, { source_label: e.target.value })} className="bg-white border-purple-200" placeholder="Source label e.g. MoSPI website / PIB" />
                  <Input data-testid={`section-source-urls-${idx}`} value={Array.isArray(s.source_urls) ? s.source_urls.join(", ") : s.source_urls || ""} onChange={(e) => updateSection(idx, { source_urls: e.target.value })} className="bg-white border-purple-200" placeholder="URLs to scrape (comma separated)" />
                  <label className="flex items-center gap-3 text-sm text-slate-600">
                    <Switch data-testid={`section-websearch-${idx}`} checked={!!s.use_web_search} onCheckedChange={(v) => updateSection(idx, { use_web_search: v })} />
                    <MagnifyingGlass size={16} className="text-purple-600" /> Use live web search (Google-search sources)
                  </label>
                </div>
              </div>
              <div><div className="label-caps text-slate-500 mb-1.5">Base / reference content (editable)</div>
                <Textarea data-testid={`section-content-${idx}`} value={s.content || ""} onChange={(e) => updateSection(idx, { content: e.target.value })} className={`${inputCls} min-h-[80px] font-mono-num text-xs`} placeholder="Static/base content or table rows (use ' | ' for table columns)…" /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
